import React, { useState, useEffect, useRef } from 'react';
import { addLocaleData, IntlProvider } from 'react-intl';
import { getTranslations, getLocaleData } from './getTranslations';
import { ELocale } from './locales';

export const dateConfigTable = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
};

export const dateConfigDefault: IDateFormats = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
};

interface IDateFormats {
  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
}

interface ITranslationsProviderProps {
  baseUrl: string;
  locale: ELocale;
  sources: string[];
  LoadingComponent?: React.ComponentType;
}

const RenderEmpty = () => null;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const invokeIf = <TArgs extends any[], TVal>(
  predicate: () => boolean,
  fn: (...args: TArgs) => TVal
) => (...args: TArgs) => {
  if (predicate()) {
    return fn(...args);
  }
};

export const TranslationsProvider: React.FC<ITranslationsProviderProps> = ({
  baseUrl,
  locale,
  sources,
  LoadingComponent = RenderEmpty,
  children,
}) => {
  if (!baseUrl) {
    throw new Error('No baseUrl given for <TranslationsProvider />');
  }

  // Sources are used to decide which translation files to download (e.g frontend.apps.game | frontend.apps.response)
  if (!sources) {
    throw new Error('No sources given to <TranslationsProvider />.');
  }

  const mounted = useRef(false);
  const [messages, setMessages] = useState<{ [id: string]: string }>();

  useEffect(() => {
    mounted.current = true;

    getLocaleData(locale)
      .then(invokeIf(() => mounted.current, addLocaleData))
      .then(() =>
        getTranslations(baseUrl, locale, sources).then(
          invokeIf(() => mounted.current, setMessages)
        )
      );

    return () => {
      mounted.current = false;
    };
  }, [baseUrl, locale, sources]);

  if (!messages) {
    return <LoadingComponent />;
  }

  return (
    <IntlProvider
      // This configuration doesn't work at the moment as there is a bug in react-intl version we use that doesn't allow setting defaultFormats. Bug has been fixed in newer versions. Because of this configs have been added manually to dates.
      defaultFormats={{
        time: {
          ...dateConfigDefault,
        },
      }}
      messages={messages}
      locale={locale}
      textComponent={React.Fragment}
    >
      {/* IntlProvider has Children.only, so offer a better children API */}
      <>{children}</>
    </IntlProvider>
  );
};
